<template>
  <u-input-menu
    v-model="selectedLocation"
    v-model:query="query"
    :search="searchLocations"
    :search-lazy="true"
    :debounce="300"
    :loading="isLoading"
    :placeholder="placeholder"
    :disabled="disabled"
    :name="name"
    :size="size"
    :maxlength="maxlength"
    by="id"
    option-attribute="label"
    icon="i-ri-map-pin-line"
    nullable
    :ui-menu="{base: 'scrollbar-light'}"
    @close="restoreSelectedLabel"
  >
    <template #option="{option}">
      <u-icon name="i-ri-map-pin-line" class="h-4 w-4 shrink-0 text-slate-400" />
      <span class="truncate">{{ option.label }}</span>
    </template>

    <template #empty>
      <span class="text-slate-500">Enter at least two characters to search for a city.</span>
    </template>

    <template #option-empty>
      <span class="text-slate-500">
        {{ emptyMessage }}
      </span>
    </template>
  </u-input-menu>
</template>

<script setup>
  const props = defineProps({
    placeholder: {
      type: String,
      default: 'City, State/Province'
    },
    disabled: Boolean,
    name: String,
    size: String,
    maxlength: {
      type: [Number, String],
      default: SESSION_LOCATION_MAX_LENGTH
    }
  });

  const location = defineModel({type: String, default: ''});
  const query = ref('');
  const isLoading = ref(false);
  const hasLookupError = ref(false);
  const requestId = ref(0);

  function savedLocationOption(value) {
    const label = value?.trim();
    return label
      ? {id: `saved-location:${label}`, label}
      : null;
  }

  const selectedLocation = ref(savedLocationOption(location.value));
  const emptyMessage = computed(() => {
    if (isLoading.value) {
      return 'Searching for cities…';
    }

    if (query.value.trim().length < 2) {
      return 'Enter at least two characters to search for a city.';
    }

    if (hasLookupError.value) {
      return 'Location search is unavailable. Please try again.';
    }

    return 'No cities found.';
  });

  async function searchLocations(search) {
    const normalizedSearch = search.trim();
    const currentRequestId = requestId.value;
    hasLookupError.value = false;

    if (normalizedSearch.length < 2) {
      isLoading.value = false;
      return [];
    }

    isLoading.value = true;

    try {
      const response = await useLocalApi().fetch('/locations', {
        params: {
          query: {q: normalizedSearch}
        }
      });

      return currentRequestId === requestId.value
        ? response.locations || []
        : [];
    } catch {
      if (currentRequestId === requestId.value) {
        hasLookupError.value = true;
      }
      return [];
    } finally {
      if (currentRequestId === requestId.value) {
        isLoading.value = false;
      }
    }
  }

  function restoreSelectedLabel() {
    query.value = '';
  }

  watch(selectedLocation, selected => {
    location.value = selected?.label || '';
    query.value = '';
  });

  watch(query, value => {
    requestId.value += 1;
    hasLookupError.value = false;

    if (value.trim().length < 2) {
      isLoading.value = false;
    }
  });

  watch(location, value => {
    if ((selectedLocation.value?.label || '') === (value || '')) {
      return;
    }

    selectedLocation.value = savedLocationOption(value);
  });
</script>
